import {
  ATLAS_MARKET_NAME,
  EVMCHAIN_MARKET_NAME,
  type IMarketCurrency,
  MARKET_CURRENCY,
  useObservableValue,
  useSubscription,
  wsScanToDoubleMap,
} from '@talos/kyoko';
import { createContext, memo, type PropsWithChildren, useContext, useMemo } from 'react';
import { asyncScheduler, type Observable, shareReplay, throttleTime } from 'rxjs';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export interface MarketCurrenciesContextProps {
  marketCurrenciesByMarket: Map<string, Map<string, IMarketCurrency>>;
  marketCurrenciesByMarketObs: Observable<Map<string, Map<string, IMarketCurrency>>>;
}

const MarketCurrenciesContext = createContext<MarketCurrenciesContextProps | null>(null);
MarketCurrenciesContext.displayName = 'MarketCurrenciesContext';

export function useMarketCurrencies() {
  const context = useContext(MarketCurrenciesContext);
  if (context == null) {
    throw new Error('Missing MarketCurrenciesContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

/**
 * AS WRITTEN THIS IS NOT PULLING IN ALL MARKET CURRENCIES
 * It only pulls EVMCHAIN (and Atlas when EnableAtlasWalletMonitoring is on) for performance reasons.
 * If we need all market currencies, we need to create a better pattern for loading them.
 */
export const MarketCurrenciesProvider = memo(function MarketCurrenciesProvider(props: PropsWithChildren<unknown>) {
  const { enableAtlasWalletMonitoring } = useFeatureFlag();

  const request = useMemo(
    () => ({
      Markets: enableAtlasWalletMonitoring ? [EVMCHAIN_MARKET_NAME, ATLAS_MARKET_NAME] : [EVMCHAIN_MARKET_NAME],
      name: MARKET_CURRENCY,
      tag: 'MarketCurrenciesProvider',
      limit: 5000,
    }),
    [enableAtlasWalletMonitoring]
  );

  const { data } = useSubscription<IMarketCurrency>(request);

  const marketCurrenciesByMarketObs = useMemo(
    () =>
      data.pipe(
        wsScanToDoubleMap({
          getKey1: mc => mc.Market,
          getKey2: mc => mc.Currency,
          newInnerMapsEachUpdate: true,
          newOuterMapEachUpdate: true,
        }),
        throttleTime(2000, asyncScheduler, { leading: true, trailing: true }),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
    [data]
  );

  const marketCurrenciesByMarket = useObservableValue(
    () => marketCurrenciesByMarketObs,
    [marketCurrenciesByMarketObs],
    new Map<string, Map<string, IMarketCurrency>>()
  );

  const value = useMemo(
    () => ({ marketCurrenciesByMarket, marketCurrenciesByMarketObs }),
    [marketCurrenciesByMarket, marketCurrenciesByMarketObs]
  );

  return <MarketCurrenciesContext.Provider value={value}>{props.children}</MarketCurrenciesContext.Provider>;
});
